import { useCallback } from "react";
import { useLocation } from "react-router";

import { useApp } from "../state/store";
import type { StatusKind } from "../ui";

export interface ScreenStatus {
  message: string;
  kind: StatusKind;
}

const EMPTY: ScreenStatus = { message: "", kind: "" };

/**
 * The status line for whichever screen is asking, and a way to write to it.
 *
 * The message lives in the store addressed by path -- see `screenStatus` there for why it
 * is not React Router navigation state. Nothing here is an effect: the status *is* store
 * state, so a screen renders whatever is addressed to it and ignores anything that is not.
 */
export function useScreenStatus(): [ScreenStatus, (message: string, kind?: StatusKind) => void] {
  const { pathname } = useLocation();
  const entry = useApp((s) => s.screenStatus);
  const status = entry && entry.path === pathname ? entry : EMPTY;
  const say = useCallback(
    (message: string, kind: StatusKind = "") =>
      useApp.getState().setScreenStatus({ path: pathname, message, kind }),
    [pathname]
  );
  return [status, say];
}

/**
 * Leave a message on a screen that is not up yet, for the navigation about to put it there.
 * Enrolment finishing hands home a link warning this way.
 */
export function sayOn(path: string, message: string, kind: StatusKind = ""): void {
  useApp.getState().setScreenStatus({ path, message, kind });
}
